/* component-cart.js — window.allplants.cart
   Lean optimistic wrapper over the Shopify Cart AJAX API.

   - Keeps an in-memory cart, updates it optimistically for instant UI,
     then reconciles with the server response.
   - Broadcasts `cart:updated` on document with detail { cart, optimistic }.
   - Box meals are added with a line-item property { _box: 'build-a-box' };
     properties beginning with "_" are hidden from the customer.

   This is the theme's reusable cart layer — the header count, a cart drawer,
   etc. can all listen to `cart:updated` rather than talking to the API directly.

   Public API:
     await cart.ensureInitialized()
     await cart.get()                                   -> cart object
     await cart.add({ id, quantity, properties, image })
     await cart.setQuantity({ id, properties, quantity })  // 0 removes the line
     await cart.changeQuantity({ id, properties, delta, meta })  // relative +/- change
     await cart.updateAttributes({ key: value, ... })

   All mutations run through a serial queue, so overlapping/rapid calls apply
   in order and a slow response can't overwrite newer state.
*/
window.allplants = window.allplants || {};

(function () {
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  const url = (path) => ROOT.replace(/\/$/, "") + path;
  const JSON_HEADERS = { "Content-Type": "application/json", Accept: "application/json" };

  // Stable identity for a line: variant id + its properties.
  function lineKey(id, properties) {
    return String(id) + ":" + JSON.stringify(properties || {});
  }

  class OptimisticCart {
    constructor() {
      this._cart = null;
      this._initPromise = null;
      // Serialises mutations: each op (with its network reconcile) finishes
      // before the next starts, so overlapping clicks can't race and a slow
      // response can't overwrite newer cart state.
      this._chain = Promise.resolve();
    }

    // Run a mutation strictly after the previous one. Failures are isolated so
    // one rejected op doesn't break the queue for the next.
    _enqueue(task) {
      const run = this._chain.then(() => task());
      this._chain = run.then(
        () => {},
        () => {},
      );
      return run;
    }

    ensureInitialized() {
      if (this._cart) return Promise.resolve(this._cart);
      if (!this._initPromise) {
        this._initPromise = fetch(url("/cart.js"), { headers: { Accept: "application/json" } })
          .then((r) => r.json())
          .then((cart) => {
            this._cart = cart;
            this._emit(false);
            return cart;
          });
      }
      return this._initPromise;
    }

    get() {
      return this._cart ? Promise.resolve(this._cart) : this.ensureInitialized();
    }

    _emit(optimistic) {
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          bubbles: true,
          detail: { cart: this._cart, optimistic: optimistic },
        }),
      );
    }

    _findItem(id, properties) {
      if (!this._cart || !Array.isArray(this._cart.items)) return null;
      const key = lineKey(id, properties);
      return (
        this._cart.items.find(
          (it) => lineKey(it.variant_id || it.id, it.properties) === key,
        ) || null
      );
    }

    _recount() {
      if (!this._cart) return;
      this._cart.item_count = this._cart.items.reduce((s, it) => s + it.quantity, 0);
      this._cart.total_price = this._cart.items.reduce(
        (s, it) => s + it.quantity * (it.price || 0),
        0,
      );
    }

    add(opts) {
      return this._enqueue(() => this._addImpl(opts));
    }

    // Relative change: computes the target quantity from the freshest in-memory
    // state at execution time (inside the queue), so rapid +/- clicks can't lose
    // updates the way stale absolute quantities do.
    changeQuantity(opts) {
      return this._enqueue(() => this._changeImpl(opts));
    }

    async _changeImpl({ id, properties = {}, delta = 0, meta = null }) {
      await this.ensureInitialized();
      const existing = this._findItem(id, properties);
      const current = existing ? existing.quantity : 0;
      const next = current + delta;
      if (next > 0 && !existing) {
        return this._addImpl({
          id,
          quantity: next,
          properties,
          image: meta && meta.image,
          title: meta && meta.title,
          price: meta && meta.price,
        });
      }
      return this._setQtyImpl({ id, properties, quantity: next });
    }

    async _addImpl({ id, quantity = 1, properties = {}, image = null, title = "", price = 0 }) {
      await this.ensureInitialized();

      // Optimistic: bump (or create) the line in memory and notify listeners now.
      const existing = this._findItem(id, properties);
      if (existing) {
        existing.quantity += quantity;
      } else {
        // Shopify's cart returns newly added line items first, so mirror that
        // here (unshift, not push) to keep the optimistic order in sync with the
        // server order and avoid a slot reorder flicker after _refresh().
        this._cart.items.unshift({
          id: id,
          variant_id: id,
          quantity: quantity,
          properties: properties,
          image: image,
          title: title,
          price: price,
        });
      }
      this._recount();
      this._emit(true);

      // Network: add, then reconcile against the authoritative cart.
      try {
        const res = await fetch(url("/cart/add.js"), {
          method: "POST",
          headers: JSON_HEADERS,
          body: JSON.stringify({ items: [{ id: id, quantity: quantity, properties: properties }] }),
        });
        if (!res.ok) throw new Error("cart/add failed: " + res.status);
        await this._refresh();
      } catch (err) {
        console.error("[allplants.cart] add failed", err);
        await this._refresh();
        throw err;
      }
    }

    setQuantity(opts) {
      return this._enqueue(() => this._setQtyImpl(opts));
    }

    async _setQtyImpl({ id, properties = {}, quantity }) {
      await this.ensureInitialized();

      const idx = this._cart.items.findIndex(
        (it) => lineKey(it.variant_id || it.id, it.properties) === lineKey(id, properties),
      );
      if (idx === -1) {
        // Nothing to change yet; treat a positive quantity as an add.
        if (quantity > 0) return this._addImpl({ id, quantity, properties });
        return;
      }

      // Optimistic update by line index (unambiguous even with duplicate variants).
      if (quantity <= 0) {
        this._cart.items.splice(idx, 1);
      } else {
        this._cart.items[idx].quantity = quantity;
      }
      this._recount();
      this._emit(true);

      try {
        const res = await fetch(url("/cart/change.js"), {
          method: "POST",
          headers: JSON_HEADERS,
          body: JSON.stringify({ line: idx + 1, quantity: Math.max(0, quantity) }),
        });
        if (!res.ok) throw new Error("cart/change failed: " + res.status);
        this._cart = await res.json();
        this._emit(false);
      } catch (err) {
        console.error("[allplants.cart] setQuantity failed", err);
        await this._refresh();
        throw err;
      }
    }

    updateAttributes(attributes) {
      return this._enqueue(() => this._updateAttrsImpl(attributes));
    }

    async _updateAttrsImpl(attributes) {
      await this.ensureInitialized();
      const res = await fetch(url("/cart/update.js"), {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify({ attributes: attributes }),
      });
      this._cart = await res.json();
      this._emit(false);
      return this._cart;
    }

    async _refresh() {
      const res = await fetch(url("/cart.js"), { headers: { Accept: "application/json" } });
      this._cart = await res.json();
      this._emit(false);
      return this._cart;
    }
  }

  window.allplants.cart = window.allplants.cart || new OptimisticCart();
})();
